"use client";

import { useMemo, useRef, useState } from "react";
import { DocumentPreview } from "@/components/tool-kit/document-preview";
import {
  Checkbox,
  Panel,
  PillGroup,
  SelectField,
  TextField,
} from "@/components/tool-kit/primitives";
import { BUTTON_SECONDARY, BUTTON_SMALL } from "@/components/tool-kit/styles";
import { useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  buildPeerReview,
  type PeerCriterion,
  type PeerQuestionStyle,
  type PeerReviewInput,
} from "@/lib/tool-kit/essay-templates/peer-review";

/** English strings. A translated content file overrides these through `ui`. */
export const PEER_REVIEW_FORM_GENERATOR_STRINGS = {
  tryExample: "Try an example",
  clear: "Start over",
  setupHeading: "The assignment",
  assignmentLabel: "Assignment type",
  assignments: {
    essay: "Argumentative essay",
    research: "Research paper",
    lab: "Lab report",
    narrative: "Narrative or personal essay",
    presentation: "Presentation",
  },
  assignmentHint:
    "Choosing a type loads its usual criteria, which you can then edit.",
  titleLabel: "Form title",
  titlePlaceholder: "e.g. Peer Review: Op-Ed Draft 1",
  criteriaHeading: "Criteria",
  criterionNameLabel: "Criterion {n}",
  criterionQuestionLabel: "Question for the reviewer",
  addCriterion: "Add a criterion",
  removeCriterion: "Remove",
  removeCriterionLabel: "Remove criterion {n}",
  formatHeading: "Format",
  styleLabel: "Question style",
  styles: {
    both: "Rating scale and open questions",
    rating: "Rating scale only",
    open: "Open questions only",
  },
  scaleLabel: "Rating scale",
  scales: { "4": "1 to 4", "5": "1 to 5" },
  linesLabel: "Lines for each written answer",
  headerLabel: "Include writer, reviewer, and date lines",
  overallLabel: "Include questions about the whole draft",
  fileName: "peer-review-form",
  document: {
    heading: "Your peer review form",
    prompt: "To write",
    guidance: "Note",
    copy: "Copy",
    copied: "Copied!",
    downloadText: "Download .txt",
    downloadMarkdown: "Download .md",
    print: "Print",
    remaining: "one: part still to write|other: parts still to write",
    complete: "Ready to print or copy.",
  },
  defaultTitle: "Peer Review Form",
  fields: {
    writer: "Writer",
    reviewer: "Reviewer",
    draft: "Draft title",
    date: "Date",
  },
  instructions:
    "Read the whole draft once before you write anything. Comment on the writing, not the writer, and point to specific sentences or paragraphs so the writer can find what you mean.",
  headings: {
    rating: "Ratings",
    open: "Questions",
    overall: "The Whole Draft",
  },
  criterionColumn: "Criterion",
  scaleNote: "Tick one box in each row. {scale}",
  scale4: ["Not yet", "Developing", "Good", "Strong"],
  scale5: ["Missing", "Weak", "Adequate", "Good", "Excellent"],
  overallQuestions: [
    "What is the strongest part of this draft? Be specific.",
    "What one change would improve the draft the most?",
  ],
  essayCriteria: [
    "Thesis",
    "Evidence",
    "Organization",
    "Citations",
    "Mechanics",
  ],
  essayQuestions: [
    "Is the main claim clear, specific, and arguable? Underline it; if you can't find it, say so.",
    "Is every claim backed by specific, relevant evidence? Name one claim that needs more support.",
    "Does each paragraph make one point, in a logical order, with clear transitions?",
    "Is every source cited in the text and listed in the references, in the required style?",
    "Are grammar, spelling, and punctuation clean enough not to distract? Note any error that repeats.",
  ],
  researchCriteria: [
    "Research question",
    "Use of sources",
    "Analysis",
    "Organization",
    "Citations and format",
  ],
  researchQuestions: [
    "Is the research question or thesis clear, focused, and stated early?",
    "Does the paper synthesize sources rather than summarize them one by one? Are they credible and current?",
    "Does the writer explain what the evidence means, not just report it?",
    "Do the headings and paragraphs move logically from the question to the conclusion?",
    "Are the in-text citations and the reference list complete and consistent with the required style?",
  ],
  labCriteria: [
    "Aim and hypothesis",
    "Method",
    "Results",
    "Discussion",
    "Conventions",
  ],
  labQuestions: [
    "Are the aim and a testable hypothesis stated clearly in the introduction?",
    "Could you repeat the experiment from the method alone? What detail is missing?",
    "Are tables and figures numbered, titled, labeled with units, and referred to in the text?",
    "Does the discussion explain the results, address sources of error, and say whether the hypothesis was supported?",
    "Are tense, units, and significant figures used correctly and consistently?",
  ],
  narrativeCriteria: [
    "Focus",
    "Detail",
    "Structure and pacing",
    "Voice",
    "Reflection",
  ],
  narrativeQuestions: [
    "Is it clear what the story is about and why it matters to the writer?",
    "Which moment is most vivid? Where would sensory detail or dialogue help?",
    "Does the story slow down for the key moments and move quickly through the rest?",
    "Does the writing sound like a real person? Point to the sentence where the voice is strongest.",
    "Does the ending show what the writer learned or how they changed?",
  ],
  presentationCriteria: [
    "Content",
    "Organization",
    "Delivery",
    "Visual aids",
    "Sources",
  ],
  presentationQuestions: [
    "Is the main message clear, and is every point supported with evidence or an example?",
    "Is there a clear opening, a logical order, and a conclusion that sums up?",
    "Is the speaker easy to hear and well paced, and do they look at the audience?",
    "Do the slides support the talk instead of repeating it? Can they be read from the back of the room?",
    "Are sources named aloud or on the slides?",
  ],
  exampleTitle: "Peer Review: Climate Policy Op-Ed, Draft 1",
  exampleCriteria: [
    "Claim",
    "Counterargument",
    "Evidence",
    "Audience and tone",
    "Attribution",
  ],
  exampleQuestions: [
    "Can you state the writer's claim in one sentence? Write it down.",
    "Does the draft answer the strongest objection fairly, or wave it away?",
    "Which statistic or example is most convincing, and which one needs a source?",
    "Would a reader who disagrees keep reading? Point to a sentence that might lose them.",
    "Is every fact attributed to a named source, as the assignment requires?",
  ],
} as const;

type Assignment = keyof typeof PEER_REVIEW_FORM_GENERATOR_STRINGS.assignments;

const ASSIGNMENTS: readonly Assignment[] = [
  "essay",
  "research",
  "lab",
  "narrative",
  "presentation",
];
const STYLES: readonly PeerQuestionStyle[] = ["both", "rating", "open"];
const SCALES = ["4", "5"] as const;
const LINE_OPTIONS = ["2", "3", "4", "5"] as const;
const MAX_CRITERIA = 8;

type Strings = typeof PEER_REVIEW_FORM_GENERATOR_STRINGS;
type Settings = Omit<PeerReviewInput, "title" | "criteria">;

const DEFAULT_SETTINGS: Settings = {
  style: "both",
  scale: "4",
  lines: "3",
  header: true,
  overall: true,
};

/** Each assignment type's default criteria: the names, and the question for each. */
const DEFAULT_KEYS = {
  essay: ["essayCriteria", "essayQuestions"],
  research: ["researchCriteria", "researchQuestions"],
  lab: ["labCriteria", "labQuestions"],
  narrative: ["narrativeCriteria", "narrativeQuestions"],
  presentation: ["presentationCriteria", "presentationQuestions"],
} as const;

function defaults(strings: Strings, assignment: Assignment) {
  const [namesKey, questionsKey] = DEFAULT_KEYS[assignment];
  const names: readonly string[] = strings[namesKey];
  const questions: readonly string[] = strings[questionsKey];
  return names.map((name, index) => ({
    name,
    question: questions[index] ?? "",
  }));
}

export default function PeerReviewFormGeneratorWidget() {
  const strings = useToolUi(PEER_REVIEW_FORM_GENERATOR_STRINGS);
  const nextId = useRef(0);
  const withIds = (
    rows: { name: string; question: string }[]
  ): PeerCriterion[] =>
    rows.map((row) => {
      nextId.current += 1;
      return { ...row, id: `c${nextId.current}` };
    });
  const [assignment, setAssignment] = useState<Assignment>("essay");
  const [title, setTitle] = useState("");
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [criteria, setCriteria] = useState<PeerCriterion[]>(() =>
    defaults(strings, "essay").map((row, index) => ({
      ...row,
      id: `d${index}`,
    }))
  );
  const doc = useMemo(
    () => buildPeerReview({ ...settings, title, criteria }, strings),
    [settings, title, criteria, strings]
  );
  const setSetting = <K extends keyof Settings>(key: K, value: Settings[K]) =>
    setSettings((previous) => ({ ...previous, [key]: value }));
  const updateCriterion = (id: string, patch: Partial<PeerCriterion>) =>
    setCriteria((previous) =>
      previous.map((criterion) =>
        criterion.id === id ? { ...criterion, ...patch } : criterion
      )
    );

  function chooseAssignment(next: Assignment) {
    setAssignment(next);
    setCriteria(withIds(defaults(strings, next)));
  }

  function loadExample() {
    setAssignment("essay");
    setTitle(strings.exampleTitle);
    setSettings(DEFAULT_SETTINGS);
    setCriteria(
      withIds(
        strings.exampleCriteria.map((name, index) => ({
          name,
          question: strings.exampleQuestions[index] ?? "",
        }))
      )
    );
  }

  function reset() {
    setTitle("");
    setSettings(DEFAULT_SETTINGS);
    setCriteria(withIds(defaults(strings, assignment)));
  }

  return (
    <div className="space-y-8">
      <div className="space-y-8 print:hidden">
        <div className="flex flex-wrap gap-3">
          <button
            className={BUTTON_SECONDARY}
            onClick={loadExample}
            type="button"
          >
            {strings.tryExample}
          </button>
          <button className={BUTTON_SECONDARY} onClick={reset} type="button">
            {strings.clear}
          </button>
        </div>

        <Panel title={strings.setupHeading}>
          <SelectField
            hint={strings.assignmentHint}
            id="pr-assignment"
            label={strings.assignmentLabel}
            onChange={chooseAssignment}
            options={ASSIGNMENTS.map((value) => ({
              value,
              label: strings.assignments[value],
            }))}
            value={assignment}
          />
          <TextField
            id="pr-title"
            label={strings.titleLabel}
            onChange={setTitle}
            placeholder={strings.titlePlaceholder}
            value={title}
          />
        </Panel>

        <Panel title={strings.criteriaHeading}>
          {criteria.map((criterion, index) => (
            <div
              className="grid gap-3 border-border border-b pb-4 last:border-b-0 last:pb-0 sm:grid-cols-[12rem_1fr_auto] sm:items-end"
              key={criterion.id}
            >
              <TextField
                id={`pr-name-${criterion.id}`}
                label={t(strings.criterionNameLabel, { n: index + 1 })}
                onChange={(name) => updateCriterion(criterion.id, { name })}
                value={criterion.name}
              />
              <TextField
                id={`pr-question-${criterion.id}`}
                label={strings.criterionQuestionLabel}
                onChange={(question) =>
                  updateCriterion(criterion.id, { question })
                }
                value={criterion.question}
              />
              <button
                aria-label={t(strings.removeCriterionLabel, { n: index + 1 })}
                className={`${BUTTON_SMALL} h-9`}
                disabled={criteria.length <= 1}
                onClick={() =>
                  setCriteria((previous) =>
                    previous.filter((other) => other.id !== criterion.id)
                  )
                }
                type="button"
              >
                {strings.removeCriterion}
              </button>
            </div>
          ))}
          {criteria.length < MAX_CRITERIA ? (
            <button
              className={BUTTON_SECONDARY}
              onClick={() =>
                setCriteria((previous) => [
                  ...previous,
                  ...withIds([{ name: "", question: "" }]),
                ])
              }
              type="button"
            >
              {strings.addCriterion}
            </button>
          ) : null}
        </Panel>

        <Panel title={strings.formatHeading}>
          <PillGroup
            label={strings.styleLabel}
            name="pr-style"
            onChange={(style) => setSetting("style", style)}
            options={STYLES.map((value) => ({
              value,
              label: strings.styles[value],
            }))}
            value={settings.style}
          />
          <div className="grid gap-4 sm:grid-cols-2">
            <SelectField
              id="pr-scale"
              label={strings.scaleLabel}
              onChange={(scale) => setSetting("scale", scale)}
              options={SCALES.map((value) => ({
                value,
                label: strings.scales[value],
              }))}
              value={settings.scale}
            />
            <SelectField
              id="pr-lines"
              label={strings.linesLabel}
              onChange={(lines) => setSetting("lines", lines)}
              options={LINE_OPTIONS.map((value) => ({ value, label: value }))}
              value={settings.lines}
            />
          </div>
          <div className="space-y-2">
            <Checkbox
              checked={settings.header}
              label={strings.headerLabel}
              onChange={(header) => setSetting("header", header)}
            />
            <Checkbox
              checked={settings.overall}
              label={strings.overallLabel}
              onChange={(overall) => setSetting("overall", overall)}
            />
          </div>
        </Panel>
      </div>

      <DocumentPreview
        doc={doc}
        fileName={strings.fileName}
        idPrefix="pr"
        labels={strings.document}
      />
    </div>
  );
}
